// Reads an invoice PDF in the browser and pulls out the fields the campaign
// needs. The file never leaves the computer and is not stored: pdf.js reads
// the text layer, and the parser below works on positioned text.

export interface PdfTextItem {
  text: string;
  x: number;
  /** PDF coordinates: larger is higher on the page. */
  y: number;
  width: number;
  page: number;
}

export interface PdfLine {
  page: number;
  y: number;
  items: PdfTextItem[];
  text: string;
}

export interface InvoiceScan {
  /** As printed, e.g. "FT 2026/185". */
  documentNumber: string | null;
  /** The sequence the app stores, e.g. "185". */
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  purchaseOrder: string | null;
  netAmount: number | null;
  vatAmount: number | null;
  totalAmount: number | null;
  /** Whether the client's NIF appears on the invoice; null when unknown. */
  vatNumberFound: boolean | null;
}

/** Text items of every page, positioned. pdf.js is loaded on first use. */
export async function extractPdfItems(file: File): Promise<PdfTextItem[]> {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  const items: PdfTextItem[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    for (const item of content.items) {
      if (!('str' in item) || !item.str.trim()) continue;
      items.push({
        text: item.str.trim(),
        x: item.transform[4],
        y: item.transform[5],
        width: item.width,
        page: p,
      });
    }
  }
  await doc.destroy();
  return items;
}

/** Items on the same baseline (within a couple of points) form a line. */
export function groupLines(items: PdfTextItem[], tolerance = 2.5): PdfLine[] {
  const sorted = [...items].sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x);
  const lines: PdfLine[] = [];
  for (const item of sorted) {
    const line = lines.find(l => l.page === item.page && Math.abs(l.y - item.y) <= tolerance);
    if (line) line.items.push(item);
    else lines.push({ page: item.page, y: item.y, items: [item], text: '' });
  }
  for (const line of lines) {
    line.items.sort((a, b) => a.x - b.x);
    line.text = line.items.map(i => i.text).join(' ').replace(/\s+/g, ' ').trim();
  }
  return lines.sort((a, b) => a.page - b.page || b.y - a.y);
}

// ── Values ───────────────────────────────────────────────────────────────────

const DATE_RE = /\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b|\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})\b/;
const MONEY_RE = /-?\d{1,3}(?:[.,\s  ]\d{3})+(?:[.,]\d{2})?|-?\d+[.,]\d{2}\b|-?\d+(?=\s*(?:€|EUR)\b)/;

export function parseDate(text: string): string | null {
  const m = text.match(DATE_RE);
  if (!m) return null;
  const [y, mo, d] = m[1] ? [m[1], m[2], m[3]] : [m[6], m[5], m[4]];
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** "1.230,50" / "1 230,50" / "1230.50" / "1,230.50" → 1230.5 */
export function parseMoney(text: string): number | null {
  const m = text.match(MONEY_RE);
  if (!m) return null;
  let s = m[0].replace(/[\s  ]/g, '');
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > lastDot) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma && lastComma !== -1) {
    s = s.replace(/,/g, '');
  } else if (lastDot !== -1 && s.length - lastDot - 1 === 3) {
    // "1.230" with no decimals: a thousands dot
    s = s.replace(/\./g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

// ── Labelled fields ──────────────────────────────────────────────────────────

/**
 * The value for a label: the rest of its line, else the item right below it
 * (invoices often print a row of headers with the values underneath).
 */
function findLabeled<T>(
  lines: PdfLine[],
  label: RegExp,
  read: (text: string) => T | null,
  { last = false }: { last?: boolean } = {}
): T | null {
  const found: T[] = [];
  lines.forEach((line, index) => {
    for (const item of line.items) {
      const match = item.text.match(label);
      if (!match) continue;

      // Same line: whatever follows the label
      const afterInItem = item.text.slice((match.index || 0) + match[0].length);
      const following = line.items.filter(i => i.x > item.x).map(i => i.text).join(' ');
      const sameLine = read(`${afterInItem} ${following}`.replace(/^[\s:.º°-]+/, ''));
      if (sameLine !== null) { found.push(sameLine); return; }

      // Below: the closest item under the label, on the next two lines
      const left = item.x - 20;
      const right = item.x + Math.max(item.width, 40) + 30;
      for (const below of lines.slice(index + 1, index + 3)) {
        if (below.page !== line.page) break;
        const candidates = below.items
          .filter(i => i.x + i.width >= left && i.x <= right)
          .sort((a, b) => Math.abs(a.x - item.x) - Math.abs(b.x - item.x));
        for (const c of candidates) {
          const value = read(c.text);
          if (value !== null) { found.push(value); return; }
        }
      }
    }
  });
  if (found.length === 0) return null;
  return last ? found[found.length - 1] : found[0];
}

const ISSUE_DATE = /^(data(?!\s+(de\s+)?(vencimento|limite|pagamento))(\s+(de\s+)?(emiss[aã]o|do\s+documento))?|emiss[aã]o|date)(?=[\s:]|$)/i;
const DUE_DATE = /(data\s+(de\s+)?)?vencimento|data\s+limite(\s+de\s+pagamento)?|due\s+date|v[aá]lid[ao]\s+at[eé]/i;
const PURCHASE_ORDER = /requisi[cç][aã]o|ordem\s+de\s+compra|(n(ota|\.?º)?\s+de\s+)?encomenda|v\/\s*ref(\.|er[eê]ncia)?|purchase\s+order|^p\.?\s?o\.?:?$|^po\s*n\.?º?:?$/i;
const NET = /total\s+(il[ií]quido|l[ií]quido|sem\s+iva)|incid[eê]ncia|base\s+tribut[aá]vel|^subtotal|^mercadorias?\s*\/?\s*servi[cç]os/i;
const VAT_TOTAL = /^(total|valor)\s+(de\s+|do\s+)?iva(?=[\s:]|$)|^iva(?=\s*:?\s*$|\s+[\d.]+,\d{2})/i;
// "Total", "Total a pagar", "Total (EUR)" — not "Total IVA", "Total ilíquido", …
const GRAND_TOTAL = /^total(?!\s+(de\s+|do\s+)?(iva|il[ií]quido|l[ií]quido|sem|desc|ret|merc|serv|imp|qtd|quant))(\s+(a\s+pagar|documento|geral|\(eur\)|eur|€))?(?=[\s:]|$)/i;

/** The first code with a digit among the next couple of words ("N.º 123" → "123"). */
function readPurchaseOrder(text: string): string | null {
  const words = text.trim().split(/\s+/).slice(0, 2);
  for (const word of words) {
    const token = word.replace(/[.;,:]+$/, '');
    if (!token || token.length > 40 || !/\d/.test(token) || parseDate(token)) continue;
    if (/^(n\.?º|n\/a|-|—|data|vencimento|v\/)$/i.test(token)) continue;
    return token;
  }
  return null;
}

// ── Purchase order in the notes ("Observações") ──────────────────────────────

const NOTES_LABEL = /^(observa[cç](?:[oõ]es|[aã]o)|obs\.?|notas?|coment[aá]rios?)(?=[\s:]|$)/i;
// Where the notes end: totals, tax summary, bank details, footer
const NOTES_END = /^(total|subtotal|mercadoria|descontos?|iva\b|resumo|taxa|incid[eê]ncia|iban|nib|swift|bic|coordenadas|banco|processado|p[aá]gina|assinatura|motivo)/i;
// "PO: 4200130069", "Ordem de compra n.º PO-2026-1", "Encomenda POPT5461", "OC 123"
const PO_LABELLED = /(?:\bp\.?\s?o\b\.?|ordem\s+de\s+compra|\bo\.?\s?c\b\.?|purchase\s+order|nota\s+de\s+encomenda|(?:n\.?\s*º?\s*(?:de\s+)?)?encomenda|requisi[cç][aã]o|pedido\s+de\s+compra|v\/\s*ref(?:\.|er[eê]ncia)?)\s*(?:n\.?\s*º|n[uú]mero|nr\.?|no\.?|#)?\s*[:#.\-–]?\s*([A-Za-z0-9][A-Za-z0-9./_-]*\d[A-Za-z0-9./_-]*)/i;
// Codes that are a purchase order without a label: PO-prefixed, or SAP's 45/42…
const PO_BARE = /\b(PO[A-Z]{0,3}[-_]?\d[\w./-]*|4[25]\d{8})\b/;

/** The text of the notes block: the rest of its line and the lines below. */
export function notesText(lines: PdfLine[]): string {
  const parts: string[] = [];
  lines.forEach((line, index) => {
    const label = line.items.findIndex(item => NOTES_LABEL.test(item.text));
    if (label < 0) return;
    const first = line.items[label];
    // Only the notes column: totals often sit to the right on the same lines
    const inColumn = (i: PdfTextItem) => i.x >= first.x - 20 && i.x < first.x + 270;
    const rest = [first.text.replace(NOTES_LABEL, ''), ...line.items.slice(label + 1).filter(inColumn).map(i => i.text)].join(' ').trim();
    if (rest) parts.push(rest);
    for (const below of lines.slice(index + 1, index + 7)) {
      const under = below.items.filter(inColumn).map(i => i.text).join(' ');
      if (below.page !== line.page || NOTES_END.test(under)) break;
      if (under) parts.push(under);
    }
  });
  return parts.join('\n').replace(/^[\s:]+/, '').trim();
}

export function purchaseOrderFromNotes(notes: string): string | null {
  if (!notes) return null;
  const labelled = notes.match(PO_LABELLED);
  if (labelled) return labelled[1].replace(/[.,;:]+$/, '');
  return notes.match(PO_BARE)?.[1] ?? null;
}

// ── The invoice ──────────────────────────────────────────────────────────────

const DOC_NUMBER = /\b(FT|FR|FS|FA)\s*([A-Za-z0-9-]*)\s*\/\s*(\d+)\b/;

export function parseInvoice(lines: PdfLine[], clientVat?: string | null): InvoiceScan {
  const allText = lines.map(l => l.text).join('\n');

  const doc = allText.match(DOC_NUMBER);
  let documentNumber: string | null = null;
  let invoiceNumber: string | null = null;
  if (doc) {
    documentNumber = `${doc[1]} ${doc[2] ? `${doc[2]}/` : ''}${doc[3]}`.replace(/\s+/g, ' ');
    invoiceNumber = doc[3];
  } else {
    const numbered = findLabeled(lines, /fatura\s*(n\.?\s*º?|n[uú]mero|nr\.?)/i, t => t.match(/\b[\w/-]*\d+\b/)?.[0] ?? null);
    if (numbered) {
      documentNumber = numbered;
      invoiceNumber = numbered.split('/').pop() || numbered;
    }
  }

  const issueDate = findLabeled(lines, ISSUE_DATE, parseDate);
  const dueDate = findLabeled(lines, DUE_DATE, parseDate);
  // A purchase order field in the header; Moloni puts it in the notes
  const purchaseOrder = findLabeled(lines, PURCHASE_ORDER, readPurchaseOrder)
    ?? purchaseOrderFromNotes(notesText(lines));

  // Totals sit at the end; the last one wins over line-item columns
  const vatAmount = findLabeled(lines, VAT_TOTAL, parseMoney, { last: true });
  let totalAmount = findLabeled(lines, GRAND_TOTAL, parseMoney, { last: true });
  let netAmount = findLabeled(lines, NET, parseMoney, { last: true });
  if (netAmount === null && totalAmount !== null && vatAmount !== null) {
    netAmount = Math.round((totalAmount - vatAmount) * 100) / 100;
  }
  if (totalAmount === null && netAmount !== null && vatAmount !== null) {
    totalAmount = Math.round((netAmount + vatAmount) * 100) / 100;
  }

  const vat = (clientVat || '').replace(/\D/g, '');
  const vatNumberFound = vat.length >= 6 ? allText.replace(/\D/g, ' ').split(/\s+/).some(t => t.endsWith(vat)) : null;

  return { documentNumber, invoiceNumber, issueDate, dueDate, purchaseOrder, netAmount, vatAmount, totalAmount, vatNumberFound };
}

export async function scanInvoicePdf(file: File, clientVat?: string | null): Promise<InvoiceScan> {
  return parseInvoice(groupLines(await extractPdfItems(file)), clientVat);
}
